// import AuthHeader from "@/components/AuthHeader";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { login } from "../api/auth";
import { Eye, EyeOff } from "lucide-react"; // for password toggle
import { motion, AnimatePresence } from "framer-motion"; // smooth fade for errors
import { useAuth } from "../contaxt/AuthContext";
import { getTokenExpirationTime } from "../utils/auth";
import Footer from "@/components/Footer"
import "../../src/styles/globle.css";
interface LoginFormData {
  email: string;
  password: string;
}
interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthStatus } = useAuth();
  const locationState = location.state as LocationState;
  const from = locationState?.from?.pathname || "/dashboard";

  const form = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setFieldErrors({});

    try {
      const res = await login(data.email, data.password);
      localStorage.setItem("access_token", res.token);
      if (res.user) {
        console.log("Storing user data in localStorage:", res.user);
        localStorage.setItem("user_data", JSON.stringify(res.user));
      }
      checkAuthStatus();
      const expirationTime = getTokenExpirationTime(res.token);
      if (expirationTime) {
        const expirationDate = new Date(expirationTime);
        console.log(`Token will expire at: ${expirationDate.toLocaleString()}`);
      }

      navigate(from, { replace: true });

    } catch (err: any) {
      console.error("Login error:", err);
      const backendError = err?.response?.data || err;
      if (backendError?.errorType === "credentials") {
        setFieldErrors({
          email: "Incorrect email or password."
        });
      } else if (backendError?.errorType === "Incorrect email or password.") {
        setFieldErrors({
          password: "Incorrect email or password."
        });
      } else if (backendError?.errorType === "email_confirmation") {
        setFieldErrors({
          email: "Incorrect email or password."
        });
      } else if (backendError?.errorType === "rate_limit") {
        setFieldErrors({
          email: backendError.error || "Too many login attempts. Please try again later"
        });
      } else {
        setFieldErrors({
          email: backendError?.error || "Something went wrong. Please try again later"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="flex items-center justify-center w-full px-4 pt-12 pb-24 md:pt-16 md:pb-32">
        <Card className="w-full max-w-md p-4 sm:p-6 lg:p-8 rounded-2xl shadow-sm">
          <CardHeader className="text-center space-y-1">
            <CardTitle className="text-2xl font-bold">Sign in to your account</CardTitle>
            <CardDescription className="text-base text-gray-600">Enter your email and password to continue
              {locationState?.from && (
                <span className="block mt-2 text-sm text-red-500">
                  You were redirected for authentication. Please sign in again.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-5 mt-6"
                >
                  {/* Email Field */}
                  <FormField
                    control={form.control}
                    name="email"
                    rules={{
                      required: "Please enter your email",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Please enter a valid email address.",
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="!text-foreground">Email address</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter your email"
                            {...field}
                            className={`h-11 px-4 text-base border border-gray-200 focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              fieldErrors.email ? "border-red-500 focus-visible:ring-red-500" : ""
                            }`}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
              
                  {/* Password Field */}
                  <FormField
                    control={form.control}
                    name="password"
                    rules={{ required: "Please enter your password" }}
                    render={({ field }) => (
                      <FormItem>
                        <div className="password-row">
                          <FormLabel className="!text-foreground">Password</FormLabel>
                          <div className="text-right">
                            <Link
                              to="/forgot-password"
                              className="text-sm text-primary/80 hover:text-primary underline-offset-2"
                            >
                              Forgot your password?
                            </Link>
                          </div>
                        </div>
              
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              {...field}
                              className={`h-11 px-4 text-base border border-gray-200 focus-visible:ring-2 focus-visible:ring-blue-500 ${
                                fieldErrors.password || fieldErrors.email
                                  ? "border-red-500 focus-visible:ring-red-500"
                                  : ""
                              }`}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                            >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </FormControl>
              
                        {/* Error under password, pushes CTA down */}
                        <AnimatePresence initial={false}>
                          {(fieldErrors.password || fieldErrors.email) && (
                            <motion.p
                              key="login-error"
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              className="text-sm text-red-600 mt-1"
                            >
                              {fieldErrors.password || fieldErrors.email}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </FormItem>
                    )}
                  />
              
                  {/* Submit Button – same feel as Forgot Password */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="
                      w-full h-11
                      text-base font-medium rounded-lg
                      mt-6
                      bg-[#005FCC] hover:bg-[#0049A8]
                      text-white
                      transition-colors
                      disabled:opacity-50
                    "
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
      <div className="footer-container-login">
        <Footer />
      </div>
    </div>

  );
};

export default Login;



